/**
 * Local Story Repository
 *
 * Implements IStoryRepository using localStorage.
 * Enforces one-story limit for free tier.
 * Maps local storage shapes to domain types.
 */

import type { StepType } from '@/types/database'
import type { LocalActiveStory, LocalStepContent } from '@/types/studio'
import type { StoryRecord, StepRecord, StoryWithSteps, RepositoryResult } from '@/types/domain'
import { STEP_ORDER, STEP_PROMPTS, calculateWordCount } from '@/types/domain'
import type { IStoryRepository, StoryUpdates } from './types'
import {
  getLocalActiveStory,
  setLocalActiveStory,
} from '@/services/localStudioStore'

/**
 * Random emoji covers for new stories
 */
const STORY_EMOJIS = ['📖', '✨', '🌟', '🚀', '🦋', '🌈', '🏰', '🐉', '🦄', '🌸', '🎭', '🎪']

function getRandomEmoji(): string {
  return STORY_EMOJIS[Math.floor(Math.random() * STORY_EMOJIS.length)]
}

/**
 * Converts local storage story to unified StoryRecord format
 */
function toStoryRecord(local: LocalActiveStory, childId: string): StoryRecord {
  return {
    id: local.id,
    childId,
    title: local.title,
    coverEmoji: local.coverEmoji,
    status: local.status,
    wordCount: local.wordCount,
    createdAt: local.createdAt,
    updatedAt: local.updatedAt,
  }
}

/**
 * Converts local storage step to unified StepRecord format
 */
function toStepRecord(
  storyId: string,
  stepType: StepType,
  local: LocalStepContent
): StepRecord {
  return {
    id: `${storyId}-${stepType}`,  // Synthetic ID for local steps
    storyId,
    stepType,
    stepOrder: STEP_ORDER[stepType],
    content: local.content,
    wordCount: local.wordCount,
    promptHint: STEP_PROMPTS[stepType],
    updatedAt: local.updatedAt,
  }
}

/**
 * Creates default empty steps for a new story
 */
function createDefaultSteps(now: string): Record<StepType, LocalStepContent> {
  const stepTypes: StepType[] = ['beginning', 'problem', 'adventure', 'climax', 'resolution']
  return Object.fromEntries(
    stepTypes.map(type => [type, { content: '', wordCount: 0, updatedAt: now }])
  ) as Record<StepType, LocalStepContent>
}

/**
 * Current child ID being operated on (set by context)
 * This is used to validate access in operations that only receive storyId
 */
let currentChildId: string | null = null

/**
 * Sets the current child context for access validation
 */
export function setCurrentChild(childId: string | null): void {
  currentChildId = childId
}

/**
 * Local Story Repository implementation
 */
export const localStoryRepository: IStoryRepository = {
  async listStories(childId: string): Promise<RepositoryResult<StoryRecord[]>> {
    try {
      const story = getLocalActiveStory(childId)
      if (!story) {
        return { success: true, data: [] }
      }
      return { success: true, data: [toStoryRecord(story, childId)] }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to list stories',
        code: 'UNKNOWN',
      }
    }
  },

  async createStory(childId: string, title: string = 'My New Story'): Promise<RepositoryResult<StoryRecord>> {
    try {
      // Enforce one-story limit
      const existing = getLocalActiveStory(childId)
      if (existing !== null) {
        return {
          success: false,
          error: 'Free tier is limited to one story. Upgrade to create more stories.',
          code: 'LIMIT_REACHED',
        }
      }

      const now = new Date().toISOString()
      const newStory: LocalActiveStory = {
        id: crypto.randomUUID(),
        title,
        coverEmoji: getRandomEmoji(),
        status: 'in_progress',
        wordCount: 0,
        createdAt: now,
        updatedAt: now,
        steps: createDefaultSteps(now),
      }

      const saved = setLocalActiveStory(childId, newStory)
      if (!saved) {
        return {
          success: false,
          error: 'Failed to save story to local storage',
          code: 'UNKNOWN',
        }
      }

      return { success: true, data: toStoryRecord(newStory, childId) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to create story',
        code: 'UNKNOWN',
      }
    }
  },

  async canCreateStory(childId: string): Promise<boolean> {
    const story = getLocalActiveStory(childId)
    // Can create if no story exists
    return story === null
  },

  async getStoryCount(childId: string): Promise<number> {
    const story = getLocalActiveStory(childId)
    return story ? 1 : 0
  },

  async getStory(storyId: string): Promise<RepositoryResult<StoryRecord | null>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const story = getLocalActiveStory(currentChildId)
      if (!story || story.id !== storyId) {
        return { success: true, data: null }
      }

      return { success: true, data: toStoryRecord(story, currentChildId) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to get story',
        code: 'UNKNOWN',
      }
    }
  },

  async getStoryWithSteps(storyId: string): Promise<RepositoryResult<StoryWithSteps | null>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const story = getLocalActiveStory(currentChildId)
      if (!story || story.id !== storyId) {
        return { success: true, data: null }
      }

      const stepTypes: StepType[] = ['beginning', 'problem', 'adventure', 'climax', 'resolution']
      const steps: StepRecord[] = stepTypes.map(stepType =>
        toStepRecord(storyId, stepType, story.steps[stepType])
      )

      return {
        success: true,
        data: {
          story: toStoryRecord(story, currentChildId),
          steps,
        },
      }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to get story with steps',
        code: 'UNKNOWN',
      }
    }
  },

  async updateStory(storyId: string, updates: StoryUpdates): Promise<RepositoryResult<StoryRecord>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const story = getLocalActiveStory(currentChildId)
      if (!story || story.id !== storyId) {
        return { success: false, error: 'Story not found', code: 'NOT_FOUND' }
      }

      // Apply updates
      if (updates.title !== undefined) story.title = updates.title
      if (updates.status !== undefined) story.status = updates.status
      if (updates.coverEmoji !== undefined) story.coverEmoji = updates.coverEmoji
      story.updatedAt = new Date().toISOString()

      const saved = setLocalActiveStory(currentChildId, story)
      if (!saved) {
        return { success: false, error: 'Failed to save story', code: 'UNKNOWN' }
      }

      return { success: true, data: toStoryRecord(story, currentChildId) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update story',
        code: 'UNKNOWN',
      }
    }
  },

  async updateStep(storyId: string, stepType: StepType, content: string): Promise<RepositoryResult<StepRecord>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const story = getLocalActiveStory(currentChildId)
      if (!story || story.id !== storyId) {
        return { success: false, error: 'Story not found', code: 'NOT_FOUND' }
      }

      const wordCount = calculateWordCount(content)
      const now = new Date().toISOString()

      // Update the step
      story.steps[stepType] = {
        content,
        wordCount,
        updatedAt: now,
      }

      // Recalculate total word count
      story.wordCount = Object.values(story.steps).reduce(
        (sum, step) => sum + step.wordCount,
        0
      )
      story.updatedAt = now

      const saved = setLocalActiveStory(currentChildId, story)
      if (!saved) {
        return { success: false, error: 'Failed to save step', code: 'UNKNOWN' }
      }

      return { success: true, data: toStepRecord(storyId, stepType, story.steps[stepType]) }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update step',
        code: 'UNKNOWN',
      }
    }
  },

  async deleteStory(storyId: string): Promise<RepositoryResult<void>> {
    try {
      if (!currentChildId) {
        return { success: false, error: 'No active child context', code: 'VALIDATION_ERROR' }
      }

      const story = getLocalActiveStory(currentChildId)
      if (!story || story.id !== storyId) {
        return { success: false, error: 'Story not found', code: 'NOT_FOUND' }
      }

      setLocalActiveStory(currentChildId, null)
      return { success: true, data: undefined }
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to delete story',
        code: 'UNKNOWN',
      }
    }
  },
}
